/**
 * UCPDashboard - Главная страница UCP (legacy)
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "../../components/UI";
import { isUnauthorizedError } from "../../api/client";
import { ucpService } from "../../api/services/ucpService";

function getPortraitSrc(character) {
    let enGender = "";
    switch (character.gender) {
        case 1:
            enGender = "human";
            break;
        case 2:
            enGender = "elf";
            break;
        case 4:
            enGender = "dwarf";
            break;
        case 6:
            enGender = "nimf";
            break;
        default:
            enGender = "human";
    }
    const enSex = character.sex === 0 ? "male" : "female";
    const skinVar = character.startSkinVariable || 1;
    return `/media/${enGender}_${enSex}_${skinVar}.png`;
}

function DashboardCharacterCard({ character, onDelete }) {
    const [imgLoaded, setImgLoaded] = useState(false);
    const imgRef = useRef(null);
    const portraitSrc = getPortraitSrc(character);

    useEffect(() => {
        setImgLoaded(false);
        const img = imgRef.current;
        if (img?.complete && img.naturalHeight > 0) setImgLoaded(true);
    }, [portraitSrc]);

    const statsLink = `../ucp/statistics?id=${character.id}`;
    const statusClass = `character-information-body character-body charact-status-${character.status}`;
    const [firstName, name, lastName] = character.name.toUpperCase().split(" ");

    return (
        <div className={statusClass}>
            <button
                type="button"
                className="character-body-delete-button"
                onClick={() => onDelete(character.id)}
                aria-label="Удалить персонажа"
            />
            {!imgLoaded && (
                <div
                    className="character-card-loader-overlay"
                    aria-busy="true"
                    aria-live="polite"
                >
                    <div
                        className="character-card-loader-spinner"
                        aria-hidden
                    />
                </div>
            )}
            <a href={statsLink}>
                <img
                    ref={imgRef}
                    src={portraitSrc}
                    alt=""
                    onLoad={() => setImgLoaded(true)}
                    onError={() => setImgLoaded(true)}
                />
            </a>
            <span>
                {firstName}
                <br />
                {name}
                <br />
                {lastName}
            </span>
        </div>
    );
}

const UCPDashboard = () => {
    const [characters, setCharacters] = useState([]);
    const [adm, setAdm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [sessionExpired, setSessionExpired] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [characterID, setCharacterID] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        setSessionExpired(false);

        try {
            const charsResult = await ucpService.getUserCharacters();
            setCharacters(charsResult?.characters ?? []);

            try {
                const adminResult = await ucpService.checkAdmin();
                setAdm((adminResult ?? 0) > 0);
            } catch (adminErr) {
                if (isUnauthorizedError(adminErr)) {
                    throw adminErr;
                }
                setAdm(false);
            }
        } catch (err) {
            setCharacters([]);
            setAdm(false);

            if (isUnauthorizedError(err)) {
                setSessionExpired(true);
                setLoadError(
                    "Сессия истекла или данные входа устарели. Войдите снова.",
                );
                return;
            }

            setLoadError(
                err.message || "Не удалось загрузить персонажей. Попробуйте ещё раз.",
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const openSubmitModal = (id) => {
        setCharacterID(id);
        setModalIsOpen(true);
    };

    const closeSubmitModal = () => {
        setModalIsOpen(false);
    };

    const handleDeleteCharacter = async () => {
        try {
            setIsDeleting(true);
            const result = await ucpService.deleteCharacter(characterID);
            if (result === true) {
                window.location.href = "/ucp";
            }
        } catch (err) {
            console.error(err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const placeholdersNeeded = Math.max(0, 4 - characters.length);

    const submitModalContent = (
        <div className="modal-content-container">
            <span className="modal-title">
                ВЫ УВЕРЕНЫ, ЧТО ХОТИТЕ УДАЛИТЬ ПЕРСОНАЖА?
            </span>
            <span>Это действие нельзя будет отменить.</span>
            <div className="modal-buttons">
                <button
                    className={`ucp-simple-yellow-button has-inline-loader ${
                        isDeleting ? "is-loading" : ""
                    }`}
                    style={{ flexGrow: 1 }}
                    type="button"
                    onClick={handleDeleteCharacter}
                    disabled={isDeleting}
                    aria-busy={isDeleting}
                >
                    <span className="inline-loader__label">ДА</span>
                    {isDeleting ? (
                        <span
                            className="btn__loader inline-loader"
                            aria-label="Загрузка"
                        >
                            <span className="btn__loader-dot"></span>
                            <span className="btn__loader-dot"></span>
                            <span className="btn__loader-dot"></span>
                        </span>
                    ) : null}
                </button>
                <button
                    className="ucp-simple-yellow-button ucp-white-modified-button"
                    style={{ flexGrow: 1 }}
                    type="button"
                    onClick={closeSubmitModal}
                >
                    НЕТ
                </button>
            </div>
        </div>
    );

    return (
        <div className="ucp-main">
            <div className="ucp-title-container">
                <h2 className="ucp-title">ВАШИ ПЕРСОНАЖИ</h2>
                {!loading && adm && (
                    <a href="../acp">
                        <button className="ucp-simple-yellow-button moder-button">
                            МОДЕРАЦИЯ
                        </button>
                    </a>
                )}
            </div>

            {loading ? (
                <div
                    className="shop-loader-container ucp-dashboard-character-loader"
                    role="status"
                    aria-live="polite"
                    aria-label="Загрузка персонажей"
                >
                    <div className="shop-loader" />
                </div>
            ) : loadError ? (
                <div className="ucp-dashboard-error" role="alert">
                    <p className="ucp-dashboard-error__text">{loadError}</p>
                    <div className="ucp-dashboard-error__actions">
                        {!sessionExpired && (
                            <button
                                type="button"
                                className="ucp-simple-yellow-button"
                                onClick={loadDashboard}
                            >
                                Повторить
                            </button>
                        )}
                        <a
                            href="/login"
                            className="ucp-simple-yellow-button ucp-white-modified-button ucp-dashboard-error__login"
                        >
                            Войти
                        </a>
                    </div>
                </div>
            ) : (
                <div className="ucp-character-cards-container">
                    <Modal
                        isOpen={modalIsOpen}
                        onClose={closeSubmitModal}
                        className="modal--ucp-delete"
                    >
                        {submitModalContent}
                    </Modal>

                    {characters.map((character) => (
                        <DashboardCharacterCard
                            character={character}
                            key={character.id}
                            onDelete={openSubmitModal}
                        />
                    ))}

                    {Array(placeholdersNeeded)
                        .fill(null)
                        .map((_, index) => (
                            <a
                                href="../ucp/create-character"
                                key={`placeholder-${index}`}
                            >
                                <div className={`character-information-body character-body`}>
                                    <div className="create-character"></div>
                                    <span>
                                        СОЗДАТЬ
                                        <br />
                                        ПЕРСОНАЖА
                                    </span>
                                </div>
                            </a>
                        ))}
                </div>
            )}
        </div>
    );
};

export default UCPDashboard;
